import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ButtonBarButtonsProp } from '../../../ButtonBar';
import styles from './useInvoiceViewButtonBarPayButton.module.scss';
import { Icon } from '../../../../blocks/icons';
import {
	InstallmentStatus,
	InvoiceViewButtonBarPayButtonInvoiceFragment,
	usePaymentSection_CreateSecretsForInvoicePaymentMutation,
} from '#types';
import { useToast } from '../../../../hooks/useToast';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore.js';
import { gql } from '@apollo/client';
import MuiModal from '../../../../mui/MuiModal/MuiModal';
import { InvoiceViewPaymentModalContent } from '../InvoiceViewPaymentModalContent';
import { InvoicePaymentSessionSecrets } from '../InvoiceViewPaymentModalContent/usePaymentSection';
import InvoiceViewPayButtonPaymentSentModal from './InvoiceViewPayButtonPaymentSentModal';
import {
	documentFollowupActionsContactFragment,
	useDocumentFollowupActions,
} from '../../../DocumentFollowupActions/useDocumentFollowupActions';
import { DocumentFollowupActions } from '../../../DocumentFollowupActions';
import rpcShared from '@rockpapercoin/rpc-shared';
import PaymentValidationErrorModal from '../../../../mui/MuiPaymentMethod/PaymentValidationErrorModal';

dayjs.extend( isSameOrBefore );

export const invoiceViewButtonBarPayButtonPaymentInstallmentsFragment = gql`
	fragment invoiceViewButtonBarPayButtonPaymentInstallments on PaymentInstallment {
		id
		status
		scheduledPaymentDate
		dueDate
	}
`;

export const invoiceViewButtonBarPayButtonInvoiceFragment = gql`
	fragment invoiceViewButtonBarPayButtonInvoice on Invoice {
		id
		paymentInstallments {
			...invoiceViewButtonBarPayButtonPaymentInstallments
		}
		contact {
			id
			customer {
				id
				email
				clientUser {
					id
					assignedPlanner {
						id
						organization {
							id
							users {
								id
								user {
									id
								}
							}
						}
					}
				}
			}
			assignedMember {
				id
				organization {
					id
					services {
						id
						name
					}
				}
			}
			...documentFollowupActionsContact
		}
		organization {
			id
			services {
				id
				name
			}
			users {
				id
				user {
					id
				}
			}
		}
		...invoiceViewPaymentModalContentInvoice
	}
	${ invoiceViewButtonBarPayButtonPaymentInstallmentsFragment }
	${ documentFollowupActionsContactFragment }
	${ InvoiceViewPaymentModalContent.fragments
		.invoiceViewPaymentModalContentInvoiceFragment }
`;

type UseInvoiceViewButtonBarExportButtonReturnType = {
	button: ButtonBarButtonsProp['buttons'];
	jsx: React.JSX.Element;
};

type UseInvoiceViewButtonBarPayButtonProps = {
	invoice?: InvoiceViewButtonBarPayButtonInvoiceFragment;
	guestToken?: string;
	setShake: React.Dispatch<React.SetStateAction<boolean>>;
	setWarning: React.Dispatch<React.SetStateAction<boolean>>;
	selectedInstallments: {
		id: string;
		dueDate?: string | Date | null;
		amountDue?: number | null;
	}[];
	setSelectedInstallments: (
		installments: {
			id: string;
			dueDate?: string | Date | null;
			amountDue?: number | null;
		}[]
	) => void;
	requestingUserIsGuest?: boolean;
	requestingUserBaseId?: string;
};

export const useInvoiceViewButtonBarPayButton = ( {
	invoice,
	guestToken,
	setShake,
	setWarning,
	selectedInstallments,
	setSelectedInstallments,
	requestingUserIsGuest,
	requestingUserBaseId,
}: UseInvoiceViewButtonBarPayButtonProps ): UseInvoiceViewButtonBarExportButtonReturnType => {
	const { showError } = useToast();
	const [ showPayModal, setShowPayModal ] = useState( false );
	const [ showPaymentSentModal, setShowPaymentSentModal ] = useState( false );
	const [ showPaymentValidationErrorModal, setShowPaymentValidationErrorModal ] =
		useState( false );
	const [ sessionSecrets, setSessionSecrets ] = useState<
		InvoicePaymentSessionSecrets | undefined
	>();
	const [ createSecretsForInvoicePayment, { loading: paymentPreflightLoading } ] =
		usePaymentSection_CreateSecretsForInvoicePaymentMutation();

	const viewingUserIsVendorMember =
		invoice?.organization?.users?.some(
			( user ) => user.user?.id === requestingUserBaseId
		) || false;
	const viewingUserIsCustomer = requestingUserBaseId
		? rpcShared.invoices.isCustomer( invoice, requestingUserBaseId )
		: false;
	const orgUser =
		invoice?.contact?.customer?.clientUser?.assignedPlanner?.organization?.users?.find(
			( user ) => user.user?.id === requestingUserBaseId
		);
	const viewingUserIsAPPA =
		requestingUserBaseId && invoice
			? rpcShared.invoices.isAssignedPlannerOrAdminOfCustomersPlannerOrg(
				invoice,
				orgUser
			  )
			: false;
	const viewingUserIsAPPAWithPaymentPerms =
		viewingUserIsAPPA &&
		!!invoice?.contact?.customer?.clientUser?.plannerOrgCanPayInvoices;
	const viewingUserIsCustomerOrAPPAWithPaymentPerms =
		viewingUserIsCustomer || viewingUserIsAPPAWithPaymentPerms;

	const { modal, onSuccess } = useDocumentFollowupActions( {
		documentType: 'invoice',
		documentId: invoice?.id || '',
		contact: invoice?.contact,
		requestingUserIsGuest: requestingUserIsGuest || false,
		requestingUserBaseId: requestingUserBaseId || '',
	} );

	const handleOnSuccess = useCallback( async () => {
		setSelectedInstallments( [] );
		const shownSomeOtherModal = await onSuccess();
		if ( !shownSomeOtherModal ) {
			setShowPaymentSentModal( true );
		}
	}, [ onSuccess, setSelectedInstallments ] );

	const payableInstallments = useMemo(
		() =>
			invoice?.paymentInstallments?.filter(
				( installment ) =>
					installment.status === InstallmentStatus.Unpaid &&
					!installment.scheduledPaymentDate &&
					installment.dueDate
			) || [],
		[ invoice?.paymentInstallments ]
	);

	const customerSelectedInstallmentsAreEarliestUnpaid = useCallback( () => {
		if ( !payableInstallments.length ) {
			return false;
		}

		payableInstallments?.sort( ( a, b ) => {
			if (
				a.dueDate &&
				b.dueDate &&
				dayjs( a.dueDate ).isSameOrBefore( dayjs( b.dueDate ) )
			) {
				return -1;
			}
			return 1;
		} );
		selectedInstallments?.sort( ( a, b ) => {
			if (
				a.dueDate &&
				b.dueDate &&
				dayjs( a.dueDate ).isSameOrBefore( dayjs( b.dueDate ) )
			) {
				return -1;
			}
			return 1;
		} );
		if (
			selectedInstallments?.every(
				( installment, index ) =>
					installment.dueDate === payableInstallments[ index ].dueDate
			)
		) {
			return true;
		}
		return false;
	}, [ payableInstallments, selectedInstallments ] );

	const openPaymentModal = useCallback(
		async ( sessionPaymentInstallmentId: string ) => {
			const response = await createSecretsForInvoicePayment( {
				variables: {
					data: { sessionPaymentInstallmentId, token: guestToken },
				},
			} );
			const secrets = response.data?.createSecretsForInvoicePayment;
			if ( secrets?.customerSecret ) {
				setSessionSecrets( {
					customerSessionClientSecret: secrets.customerSecret,
					currency: secrets.currency,
					onBehalfOf: secrets.onBehalfOf || undefined,
					excludedPaymentMethodTypes: secrets.excludedPaymentMethodTypes,
				} );
				setShowPayModal( true );
				return;
			}
			if ( secrets ) {
				showError( 'Failed to create customer session' );
				return;
			}
			if ( response.errors ) {
				const unfilteredErrors = rpcShared.errorFiltering.filterApiErrors(
					response,
					[
						'vendorHasStripeNeeds',
						'default',
						'cannotReceivePayments',
						'cannotPayDisputedInvoice',
					],
					{
						vendorHasStripeNeeds: () =>
							showError( rpcShared.strings.errorMessages.vendorHasStripeNeeds ),
						default: () => showError( rpcShared.strings.errorMessages.default ),
						cannotReceivePayments: () =>
							showError( rpcShared.strings.errorMessages.cannotReceivePayments ),
						cannotPayDisputedInvoice: () =>
							showError(
								rpcShared.strings.errorMessages.cannotPayDisputedInvoice
							),
					}
				);
				if ( ( unfilteredErrors.errors?.length ?? 0 ) > 0 ) {
					setShowPaymentValidationErrorModal( true );
				}
			}
		},
		[
			createSecretsForInvoicePayment,
			guestToken,
			showError
		]
	);

	const onRequestToPay = useCallback( async () => {
		if (
			selectedInstallments?.length &&
			customerSelectedInstallmentsAreEarliestUnpaid()
		) {
			// the customer has selected one or more installments to pay
			// and among them is the earliest unpaid installment
			await openPaymentModal( selectedInstallments[ 0 ].id );
			// also clear the shake and warning states
			setShake( false );
			setWarning( false );
		} else if (
			selectedInstallments?.length &&
			!customerSelectedInstallmentsAreEarliestUnpaid()
		) {
			// the customer has selected one or more installments to pay
			// but none of them are the earliest unpaid installment
			showError(
				'There are installments with an earlier due date that must be paid first.'
			);
		} else if (
			selectedInstallments?.length === 0 &&
			payableInstallments.length &&
			payableInstallments.length === 1
		) {
			// the customer has selected no installments
			// but there is only one installment to pay
			const installmentsToPay =
				payableInstallments?.map( ( installment ) => installment ) ?? [];
			setSelectedInstallments( installmentsToPay );
			// clear the shake and warning states
			setShake( false );
			setWarning( false );
			// show the pay modal after confirming the vendor can accept payment
			await openPaymentModal( installmentsToPay[ 0 ].id );
		} else {
			// throw an error because there are multiple installments to pay, but none have been selected
			setShake( true );
			setWarning( true );
		}
	}, [
		customerSelectedInstallmentsAreEarliestUnpaid,
		openPaymentModal,
		payableInstallments,
		selectedInstallments,
		setSelectedInstallments,
		setShake,
		setWarning,
		showError,
	] );

	// If we open the pay modal make sure to reset the warning state
	useEffect( () => {
		if ( showPayModal ) {
			setWarning( false );
		}
	}, [ showPayModal, setWarning ] );

	const button: ButtonBarButtonsProp['buttons'] = {
		PAY: {
			isVisible:
				!!invoice?.id &&
				!!payableInstallments.length &&
				viewingUserIsCustomerOrAPPAWithPaymentPerms &&
				!viewingUserIsVendorMember,
			isIconWhenSmall: false,
			variant: 'contained',
			color: 'primary',
			'data-cy': 'pay-button',
			startIcon: <Icon type={ 'pay' } />,
			loading: paymentPreflightLoading,
			onClick: onRequestToPay,
		},
	};

	const onClose = useCallback( () => {
		setShowPayModal( false );
		setSessionSecrets( undefined );
	}, [] );

	return {
		button,
		jsx: (
			<>
				<PaymentValidationErrorModal
					open={ showPaymentValidationErrorModal }
					onClose={ () => window.location.reload() }
				/>
				{ invoice?.contact?.customer.email ? (
					<>
						{ sessionSecrets ? (
							<MuiModal
								title={ 'Make a payment' }
								muiModalProps={ {
									open: showPayModal,
									onClose,
									slotProps: {
										paper: {
											'data-cy': 'pay-installments-modal',
											className: styles.modal,
										},
									},
								} }
							>
								<InvoiceViewPaymentModalContent
									invoice={ invoice }
									initialSessionSecrets={ sessionSecrets }
									selectedInstallments={ invoice.paymentInstallments?.filter(
										( installment ) =>
											selectedInstallments.some(
												( selectedInstallment ) =>
													selectedInstallment.id === installment.id
											)
									) }
									onSuccess={ handleOnSuccess }
									onClose={ onClose }
									isAnonymousView={ !!guestToken }
									guestToken={ guestToken }
								/>
							</MuiModal>
						) : null }
						<InvoiceViewPayButtonPaymentSentModal
							open={ showPaymentSentModal }
							onClose={ () => setShowPaymentSentModal( false ) }
						/>
						<DocumentFollowupActions modal={ modal } />
					</>
				) : null }
			</>
		),
	};
};
